import type * as React from 'react'
import { CommandPalette } from '@/components/shell/command-palette'
import { Rail } from '@/components/shell/rail'
import { PageMetaProvider, Topbar } from '@/components/shell/topbar'
import { useRealtime } from '@/hooks/use-realtime'
import { useUiStore } from '@/stores/ui-store'
import { cn } from '@/lib/cn'

/**
 * The frame every route renders inside.
 *
 * Three structural decisions matter here:
 *
 *  - **`useRealtime()` is called exactly once, at this level.** The hook owns
 *    an `EventSource`; mounting it in two places would open two streams, and
 *    the API would then push every customer patch twice to the same tab. The
 *    resulting status is handed down as a prop rather than re-derived.
 *  - **Nothing here scrolls — the route content does.** The rail and topbar are
 *    grid tracks with fixed extents and `<main>` gets `minmax(0, 1fr)` plus
 *    `overflow-hidden`, so it is a *bounded* box rather than a scrolling one.
 *
 *    That boundedness is load-bearing. `<main>` is a flex column, so a route
 *    that declares `flex-1 min-h-0` resolves against the viewport instead of
 *    against its own content, and the virtualised table's inner
 *    `overflow-auto` becomes the real scroll parent. If `<main>` scrolled
 *    instead, the table would stretch to its full content height, the
 *    virtualizer would measure a clientHeight equal to scrollHeight, and every
 *    one of 600 rows would mount — which is precisely the thing virtualisation
 *    exists to prevent. Routes own their own scrolling.
 *  - **Below `md` the rail track does not exist.** The grid is a single column
 *    of topbar + main; navigation moves into the portalled slide-over that
 *    `<Rail>` renders for small screens. Declaring the track and hiding its
 *    occupant would leave an `auto` column whose width depends on whatever a
 *    descendant happens to measure — one column, or none, is the only
 *    definition that cannot surprise us.
 *
 * `h-dvh`, not `h-screen`: on iOS Safari `100vh` is the *expanded* viewport, so
 * the bottom row of a `100vh` app sits behind the URL bar until you scroll.
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  const realtime = useRealtime()
  const density = useUiStore((s) => s.density)

  return (
    <PageMetaProvider>
      {/* Keyboard users land here first; the rail is long and the table is the
          reason they came. */}
      <a
        href="#main"
        className={cn(
          'sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[90]',
          'focus:rounded-md focus:border focus:border-line focus:bg-s2 focus:px-3 focus:py-1.5',
          'focus:text-[13px] focus:text-fg focus:shadow-e2'
        )}
      >
        Skip to content
      </a>

      <div
        data-density={density}
        className={cn(
          'grid h-dvh w-full overflow-hidden bg-s0',
          'grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)]',
          'md:grid-cols-[auto_minmax(0,1fr)]'
        )}
      >
        {/* Renders nothing in the grid below `md` — the sheet it also owns is
            portalled, so it never claims a track. */}
        <Rail realtime={realtime} className="md:col-start-1 md:row-span-2 md:row-start-1" />

        <Topbar realtime={realtime} className="col-start-1 row-start-1 md:col-start-2" />

        <main
          id="main"
          tabIndex={-1}
          className={cn(
            'col-start-1 row-start-2 md:col-start-2',
            'flex min-h-0 min-w-0 flex-col overflow-hidden outline-none'
          )}
        >
          {children}
        </main>
      </div>

      {/* Portalled, so it sits outside the grid and cannot affect its track
          sizing. `SessionDrawer` was mounted alongside it and is gone: it read
          the sibling fleet's `customer_sessions` and `session_logs`, which this
          console no longer looks at. Runs performed here open `RunDrawer`. */}
      <CommandPalette />
    </PageMetaProvider>
  )
}
